import React, { useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  isOrderModified,
  hasUnseenModification,
  parseModificationLog,
  normalizeRevision,
  itemRevisionSummary,
  acknowledgeRevision,
} from "@/lib/orderRevision";
import { CaretDown, CaretUp, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * REVISED · REV N panel — the master audit surface for order-level modifications.
 * Amber-outlined when the server-side ack is behind revision_number; muted +
 * collapsed once acknowledged. Renders a summary chip strip (`+N ADDED · −N
 * REMOVED · N QTY CHANGE`) computed from item-level revision stamps, plus each
 * newline-delimited entry from modification_log in newest-first order.
 *
 * "Mark as reviewed" POSTs to /api/orders/{id}/acknowledge-revision so the ack
 * is shared across every operator's browser, then bumps a local ackVersion
 * (via onAcknowledge callback) so the containing page re-derives its gating.
 */
export default function ModificationHistory({ order, onAcknowledge }) {
  const unseen = hasUnseenModification(order);
  const [expanded, setExpanded] = useState(unseen);
  const [busy, setBusy] = useState(false);

  const acknowledge = useCallback(async () => {
    if (!order?.id || busy) return;
    setBusy(true);
    try {
      const ok = await acknowledgeRevision(order);
      if (ok) toast.success("Revision reviewed");
      else toast.success("Revision reviewed (offline — will sync)");
      if (onAcknowledge) onAcknowledge();
    } finally {
      setBusy(false);
    }
  }, [order, busy, onAcknowledge]);

  if (!isOrderModified(order)) return null;

  const lines = parseModificationLog(order.modification_log);
  const rev = normalizeRevision(order.revision_number);
  const summary = itemRevisionSummary(order);
  const relTime = relativeTime(order.last_revised_at || order.updated_date);

  const containerCls = unseen
    ? "border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700"
    : "border border-zinc-200 bg-white dark:bg-zinc-900 dark:border-zinc-700";

  return (
    <Card className={`p-4 ${containerCls}`} data-testid="modification-history">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          {unseen ? (
            <Warning size={20} weight="fill" className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          ) : (
            <CheckCircle size={20} weight="fill" className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-zinc-500">
              {unseen ? "Order revised upstream — please review" : "Revision history"}
            </div>
            <div className="font-heading font-black text-lg mt-0.5 flex flex-wrap items-baseline gap-2">
              <span>Rev {rev}</span>
              {relTime && (
                <span className="text-xs font-normal text-zinc-500 tracking-normal" data-testid="revision-relative-time">
                  · {relTime}
                </span>
              )}
            </div>
            {(summary.added > 0 || summary.removed > 0 || summary.qtyChanged > 0) && (
              <SummaryChips summary={summary} unseen={unseen} />
            )}
            {unseen && (
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-2 leading-snug">
                Line items may have been added, removed, or quantity-changed since you last opened it.
                Verify the items list before picking, building, or printing labels.
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-shrink-0 text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
          data-testid="modification-history-toggle"
          aria-label={expanded ? "Collapse revision history" : "Expand revision history"}
        >
          {expanded ? <CaretUp size={18} weight="bold" /> : <CaretDown size={18} weight="bold" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800/60">
          {lines.length ? (
            <ul className="space-y-1.5" data-testid="modification-history-entries">
              {lines.map((line, i) => (
                <li
                  key={i}
                  className="text-sm font-mono leading-snug text-zinc-800 dark:text-zinc-200 break-words"
                  data-testid={`modification-entry-${i}`}
                >
                  {line}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-zinc-500 italic">
              This order has been revised {rev} time{rev === 1 ? "" : "s"} upstream, but no human-readable log entries were provided.
            </p>
          )}

          {unseen && (
            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800/60 flex justify-end">
              <Button
                onClick={acknowledge}
                disabled={busy}
                className="bg-amber-600 hover:bg-amber-700 text-white h-9"
                data-testid="modification-acknowledge"
              >
                <CheckCircle size={16} weight="bold" className="mr-1.5" /> {busy ? "Saving…" : "Mark as reviewed"}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function SummaryChips({ summary, unseen }) {
  // Compact chip strip: +N ADDED · −N REMOVED · N QTY CHANGE. Only render
  // non-zero counts so an audit-only order with zero deltas doesn't get a
  // useless strip.
  const chips = [];
  if (summary.added > 0) {
    chips.push({
      key: "added",
      text: `+${summary.added} ADDED`,
      cls: unseen
        ? "border-emerald-500 bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200 dark:border-emerald-600"
        : "border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800",
    });
  }
  if (summary.removed > 0) {
    chips.push({
      key: "removed",
      text: `−${summary.removed} REMOVED`,
      cls: unseen
        ? "border-red-500 bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200 dark:border-red-600"
        : "border-red-300 bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800",
    });
  }
  if (summary.qtyChanged > 0) {
    chips.push({
      key: "qty",
      text: `${summary.qtyChanged} QTY CHANGE${summary.qtyChanged === 1 ? "" : "S"}`,
      cls: unseen
        ? "border-amber-500 bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 dark:border-amber-600"
        : "border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800",
    });
  }
  if (!chips.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="revision-summary-chips">
      {chips.map((c) => (
        <span
          key={c.key}
          data-testid={`summary-chip-${c.key}`}
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] whitespace-nowrap ${c.cls}`}
        >
          {c.text}
        </span>
      ))}
    </div>
  );
}

/** Compact relative-time formatter for the panel header ("2 min ago", "3 hr ago", "yesterday"). */
function relativeTime(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 30) return `${day} days ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr} yr ago`;
}
